import { useRequest } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { getRegionKey } from "@certn/utils";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useLanguageOptions } from "./api/useLanguageOptions";
import { useUser } from "./api/useUser";

export function useLanguage() {
  const request = useRequest();
  const { details: userDetails } = useUser();
  const { t, i18n } = useTranslation();
  const { languageOptions } = useLanguageOptions();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const language = searchParams.get("applicant_language");
    if (language) {
      i18n.changeLanguage(language);
      return;
    }
    const locale =
      getRegionKey() === "au" ? "en-AU" : searchParams.get("locale");
    if (userDetails) {
      i18n.changeLanguage(userDetails.auth0_language);
    } else if (locale) {
      i18n.changeLanguage(locale);
    }
    document.documentElement.lang = i18n.language;
  }, [i18n, searchParams, userDetails]);

  const getLanguageLabel = (locale: string) =>
    languageOptions?.find(({ value }) => value === locale)?.display_value;

  const changeLanguage = async (locale: string) => {
    try {
      locale = getRegionKey() === "au" ? "en-AU" : locale;
      if (userDetails) {
        await request(
          "/api/internal/applicant/me/preferences/language/update/",
          {
            method: "POST",
            body: JSON.stringify({ language: locale }),
          }
        );
      }
      i18n.changeLanguage(locale);
      document.documentElement.lang = locale;
      window.location.reload();
    } catch (e) {
      console.warn("Unable to update language:", e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
    }
  };

  return {
    changeLanguage,
    getLanguageLabel,
    currentLanguageLabel: getLanguageLabel(i18n.resolvedLanguage || "en"),
  };
}
